import { Component, input, OnInit, output, signal, viewChild, viewChildren } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidDraggable } from '../../components/rid-draggable/rid-draggable';
import { RidKlankCard } from '../../components/rid-klank-card/rid-klank-card';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOption } from '../../components/rid-option/rid-option';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTextInput } from '../../components/rid-text-input/rid-text-input';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidVerticalSpacer } from '../../components/rid-vertical-spacer/rid-vertical-spacer';
import { RidWordPathGap } from '../../components/rid-word-path-gap/rid-word-path-gap';
import { RidWordPathKlank } from '../../components/rid-word-path-klank/rid-word-path-klank';
import { generateKlankOptions } from './rid-word-path.helper';

@Component({
  selector: 'rid-word-path',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidOverlayFeedback,
    RidOption,
    RidDraggable,
    RidKlankCard,
    RidWordPathKlank,
    RidWordPathGap,
    RidVerticalSpacer,
    RidTextInput,
  ],
  templateUrl: './rid-word-path.html',
  host: {
    class: 'block',
  },
})
export class RidWordPath implements OnInit {
  startWord = input.required<string[]>();
  path = input.required<{ word: string[]; image: string; audio: string }[]>();

  index = signal(0);
  assignment = signal<{
    id: string;
    options: { id: string; klank: string; consumed: boolean }[];
    showOptions: boolean;
    completed?: boolean;
    prompt: { word: string[]; image: string; audio: string };
  }>({
    id: '',
    options: [],
    showOptions: false,
    prompt: { word: [], image: '', audio: '' },
  });
  word = signal<{ id: string; klank: string; klankId: string }[]>([]);
  progress = signal(0);
  dragIndex = signal<number | undefined>(undefined);
  wideGaps = signal(false);
  target = signal<{ kind: 'GAP' | 'CARD'; index: number } | undefined>(undefined);

  klankComps = viewChildren<RidWordPathKlank>('klankComps');
  gapComps = viewChildren<RidWordPathGap>('gapComps');
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');

  finished = output<void>();

  ngOnInit(): void {
    this.assignment.set({
      id: crypto.randomUUID(),
      prompt: this.path()[this.index()],
      showOptions: false,
      options: generateKlankOptions(this.startWord().join(''), this.path()[this.index()].word.join(''))
        .map((k) => k.klank)
        .map((k) => ({ id: crypto.randomUUID(), klank: k, consumed: false })),
    });
    this.word.set(this.startWord().map((k) => ({ id: crypto.randomUUID(), klank: k, klankId: crypto.randomUUID() })));
  }

  async present(optionComp: RidOption) {
    await optionComp.present();
    this.presentOptions();
  }

  presentOptions() {
    this.assignment.update((a) => {
      a.showOptions = true;
      return a;
    });
  }

  onDragMove(draggable: RidDraggable, pointerPosition: { x: number; y: number }) {
    const el = document.elementFromPoint(pointerPosition.x, pointerPosition.y);
    if (!el) return;

    const dropTarget = el.closest<HTMLElement>('[data-drop-kind]');
    if (!dropTarget) {
      this.target.set(undefined);
      return;
    }

    const kind = dropTarget.dataset['dropKind'] as 'GAP' | 'CARD';
    const index = Number(dropTarget.dataset['dropIndex']);

    const current = this.target();
    if (current?.kind === kind && current?.index === index) return;

    this.target.set({ kind, index });

    const offset = this.calculateSnapPosition(kind, index);
    draggable.snapTo(offset.x, offset.y - 20);
  }

  calculateSnapPosition(kind: 'GAP' | 'CARD', index: number) {
    // targetEls array structure: [GAP-1, CARD0, GAP0, CARD1, GAP1, CARD2, GAP2]
    // For GAP: index -1 -> 0, index 0 -> 2, index 1 -> 4, etc. => (index + 1) * 2
    // For CARD: index 0 -> 1, index 1 -> 3, index 2 -> 5, etc. => index * 2 + 1
    if (kind === 'CARD') {
      const targetEl = this.klankComps()[index];
      return targetEl.getSnapPosition();
    } else {
      const targetEl = this.gapComps()[index + 1];
      return targetEl.getSnapPosition();
    }
  }

  grabbed(index: number) {
    this.dragIndex.set(index);
    this.wideGaps.set(true);
  }

  release() {
    const target = this.target();
    const index = this.dragIndex();

    if (target && index !== undefined) {
      const option = this.assignment().options[index];

      const result = this.evaluate(
        this.word().map((k) => k.klank),
        option.klank,
        target,
      );
      const isCorrect = this.assignment().prompt.word.join('') === result.join('');
      if (isCorrect) {
        this.overlayFeedback().showCorrect();

        this.apply(target, option.klank);

        this.consumeOption(option.id);
        delay(500).then(() => this.next());
      } else {
        this.overlayFeedback().showIncorrect();
      }
    }

    this.wideGaps.set(false);
    this.dragIndex.set(undefined);
    this.target.set(undefined);
  }

  evaluate(word: string[], klank: string, target: { kind: 'GAP' | 'CARD'; index: number }): string[] {
    if (target.kind === 'GAP') {
      word.splice(target.index + 1, 0, klank);
    } else {
      word[target.index] = klank;
    }
    return word;
  }

  apply(target: { kind: 'GAP' | 'CARD'; index: number }, klank: string) {
    if (target.kind === 'GAP') {
      this.insertAtGap(target.index, klank);
    } else {
      this.replaceCard(target.index, klank);
    }
  }

  insertAtGap(gapIndex: number, klank: string) {
    this.word.update((word) => {
      const newWord = [...word];
      newWord.splice(gapIndex + 1, 0, { id: crypto.randomUUID(), klank, klankId: crypto.randomUUID() });
      return newWord;
    });
  }

  replaceCard(cardIndex: number, klank: string) {
    this.word.update((word) => {
      const newWord = [...word];
      newWord[cardIndex].klank = klank;
      newWord[cardIndex].klankId = crypto.randomUUID();
      return newWord;
    });
  }

  consumeOption(id: string) {
    this.assignment.update((a) => {
      a.completed = true; // NOTE: this makes sure that no further interaction is possible with this assignment
      a.options.filter((o) => o.id === id).forEach((o) => (o.consumed = true));
      return a;
    });
  }

  next() {
    const nextIndex = this.index() + 1;
    if (nextIndex >= this.path().length) {
      this.finish();
      return;
    }

    this.index.set(nextIndex);
    const options = generateKlankOptions(
      this.word()
        .map((k) => k.klank)
        .join(''),
      this.path()[nextIndex].word.join(''),
    );
    this.progress.set(nextIndex);
    this.assignment.set({
      id: crypto.randomUUID(),
      prompt: this.path()[nextIndex],
      showOptions: false,
      options: options.map((k) => k.klank).map((k) => ({ id: crypto.randomUUID(), klank: k, consumed: false })),
    });
  }

  finish() {
    this.finished.emit();
  }
}
